// src/modules/dashboard/application/LoadPyGData.ts

import { DashboardRepo } from '../domain/DashboardRepo';
import { PygGrupo, PygData } from '../domain/models';

/**
 * Processed table row for P&G display
 */
export interface PygTableRow {
  concept: string;
  clave_num: string;
  value: number;
  percentage: number;
  isEmpty: boolean;
  isResult: boolean;
}

/**
 * Processed chart data segment for P&G display
 */
export interface PygChartSegment {
  concept: string;
  clave_num: string;
  percentage: number;
  value: number;
  color: string;
}

/**
 * Year-specific data for multi-year table
 */
export interface YearData {
  value: number;
  percentage: number;
  isEmpty: boolean;
}

/**
 * Processed table row for multi-year P&G display
 */
export interface MultiYearPygTableRow {
  concept: string;
  clave_num: string;
  years: { [year: string]: YearData };
  isResult: boolean;
  variation?: {
    value: number;
    percentage: number;
  };
}

/**
 * Application service to load and process P&G data
 */
export class LoadPyGData {
  repo: DashboardRepo | null;

  constructor(repo: DashboardRepo | null) {
    this.repo = repo;
  }

  /**
   * Load P&G data from repository
   */
  async load(): Promise<{ pygGrupos: PygGrupo[], pygData: PygData[] }> {
    if (!this.repo) {
      throw new Error('Repository is required for loading data');
    }
    return await this.repo.loadPygData();
  }

  /**
   * Process P&G data for a specific fiscal year (ejercicio)
   * @param pygGrupos List of P&G concept groups
   * @param pygData List of P&G data values
   * @param fiscalYear Fiscal year to filter (e.g., 2012)
   * @returns Processed table data and chart data
   */
  processDataForYear(
    pygGrupos: PygGrupo[],
    pygData: PygData[],
    fiscalYear: number
  ): { tableData: PygTableRow[], chartData: PygChartSegment[], totalSales: number, result: number } {
    // Filter data for the selected fiscal year
    const yearData = pygData.filter(item => {
      const [month, year] = item.mes_ejercicio.split('/');
      return parseInt(year) === fiscalYear;
    });

    // console.log(`[PyG] Processing data for fiscal year ${fiscalYear}, found ${yearData.length} items`);

    // Create a map of clave_num to total value (aggregating by clave_num)
    const dataMap = new Map<string, number>();
    yearData.forEach(item => {
      const currentValue = dataMap.get(item.clave_num) || 0;
      dataMap.set(item.clave_num, currentValue + item.total);
    });

    // Log all data to debug
    // console.log('[PyG] All yearData items:', yearData.length);
    const positiveItems = yearData.filter(item => item.total > 0);
    const negativeItems = yearData.filter(item => item.total < 0);
    // console.log(`[PyG] Positive items: ${positiveItems.length}, Negative items: ${negativeItems.length}`);
    if (positiveItems.length > 0) {
      // console.log('[PyG] First 5 positive items:', JSON.stringify(positiveItems.slice(0, 5), null, 2));
    }
    // console.log('[PyG] Aggregated dataMap:', Object.fromEntries(dataMap));

    // Find total sales DYNAMICALLY
    // Strategy 1: Try to find clave_num "1" first (for compatibility with old data)
    let salesItem = yearData.find(item => item.clave_num === "1");

    // Strategy 2: If not found, use the aggregated dataMap to find highest positive value
    if (!salesItem) {
      // Find the clave_num with the highest positive aggregated value
      let maxValue = 0;
      let maxClaveNum = '';

      dataMap.forEach((total, claveNum) => {
        if (total > maxValue) {
          maxValue = total;
          maxClaveNum = claveNum;
        }
      });

      if (maxValue > 0) {
        // Find any item with this clave_num to get concept name
        salesItem = yearData.find(item => item.clave_num === maxClaveNum);
        // console.log(`[PyG] Using dynamic totalSales from aggregated value: clave_num "${maxClaveNum}" = ${maxValue}`);
      }
    }

    const totalSales = salesItem ? Math.abs(dataMap.get(salesItem.clave_num) || salesItem.total) : 0;

    if (totalSales === 0) {
      console.warn(`[PyG] No sales data found for fiscal year ${fiscalYear}. Available clave_nums:`, Array.from(dataMap.keys()));
      console.warn('[PyG] DataMap values:', Object.fromEntries(dataMap));
    } else {
      // console.log(`[PyG] Total sales for ${fiscalYear}: ${totalSales} (clave_num: ${salesItem?.clave_num}, concept: "${salesItem?.concept}")`);
    }

    // Process table rows
    const tableData: PygTableRow[] = [];
    let calculatedResult = totalSales; // Start with total sales

    pygGrupos.forEach(grupo => {
      const value = dataMap.get(grupo.clave_num) || 0;
      const percentage = totalSales !== 0 ? (value / totalSales) * 100 : 0;
      const isEmpty = value === 0;

      // Calculate accumulated result (ingresos - gastos)
      if (value !== 0 && grupo.clave_num !== "1") {
        // Add or subtract based on the value sign
        calculatedResult += value;
      }

      tableData.push({
        concept: grupo.concept,
        clave_num: grupo.clave_num,
        value: value,
        percentage: percentage,
        isEmpty: isEmpty,
        isResult: false
      });
    });

    // Add result row at the end
    const resultPercentage = totalSales !== 0 ? (calculatedResult / totalSales) * 100 : 0;
    tableData.push({
      concept: "Resultado",
      clave_num: "resultado",
      value: calculatedResult,
      percentage: resultPercentage,
      isEmpty: false,
      isResult: true
    });

    // Process chart data (only concepts with values, in order for stacked chart)
    const chartData: PygChartSegment[] = this.prepareChartData(dataMap, totalSales, calculatedResult, pygGrupos);

    return {
      tableData,
      chartData,
      totalSales,
      result: calculatedResult
    };
  }

  /**
   * Prepare data for the stacked bar chart (DYNAMIC VERSION)
   * Chart shows percentages of all concepts that have data
   * Automatically adapts to different clave_num codes
   */
  private prepareChartData(
    dataMap: Map<string, number>,
    totalSales: number,
    result: number,
    pygGrupos: PygGrupo[]
  ): PygChartSegment[] {
    // Dynamic color palette for chart segments
    const colorPalette = [
      '#D35400',   // Dark Orange
      '#E67E22',   // Orange
      '#2E86AB',   // Dark Blue
      '#5DADE2',   // Light Blue
      '#9B59B6',   // Purple
      '#F39C12',   // Yellow-Orange
      '#16A085',   // Teal
      '#C0392B',   // Dark Red
      '#8E44AD',   // Dark Purple
      '#27AE60',   // Green
      '#E74C3C',   // Red
      '#BDC3C7'    // Gray
    ];

    // Create a map of clave_num to grupo info
    const grupoMap = new Map<string, PygGrupo>();
    pygGrupos.forEach(grupo => {
      grupoMap.set(grupo.clave_num, grupo);
    });

    // Collect all concepts with data
    const conceptsWithData: Array<{
      clave_num: string;
      concept: string;
      type: string;
      value: number;
      percentage: number;
    }> = [];

    // Process each entry in dataMap
    dataMap.forEach((value, claveNum) => {
      const grupo = grupoMap.get(claveNum);

      if (grupo && value !== 0) {
        const percentage = totalSales !== 0 ? (Math.abs(value) / totalSales) * 100 : 0;

        // Only add if percentage is significant (> 0) - use absolute for chart display
        if (percentage > 0) {
          conceptsWithData.push({
            clave_num: claveNum,
            concept: grupo.concept,
            type: grupo.type,
            value: value,
            percentage: percentage  // Keep absolute for chart visualization
          });
        }
      }
    });

    // Sort concepts: gastos first (by value descending), then ingresos
    conceptsWithData.sort((a, b) => {
      // Gastos (negative values) come first
      if (a.type === 'gasto' && b.type !== 'gasto') return -1;
      if (a.type !== 'gasto' && b.type === 'gasto') return 1;

      // Within same type, sort by absolute value descending
      return Math.abs(b.value) - Math.abs(a.value);
    });

    // Build chart data
    const chartData: PygChartSegment[] = conceptsWithData.map((item, index) => ({
      concept: item.concept,
      clave_num: item.clave_num,
      percentage: item.percentage,
      value: Math.abs(item.value),
      color: colorPalette[index % colorPalette.length]
    }));

    // Add result at the end
    const resultPercentage = totalSales !== 0 ? (result / totalSales) * 100 : 0;
    if (resultPercentage > 0 || result !== 0) {
      chartData.push({
        concept: 'Resultado',
        clave_num: 'resultado',
        percentage: Math.abs(resultPercentage),
        value: Math.abs(result),
        color: result >= 0 ? '#27AE60' : '#E74C3C'  // Green for profit, Red for loss
      });
    }

    return chartData;
  }

  /**
   * Get available fiscal years from pygData
   */
  getAvailableFiscalYears(pygData: PygData[]): number[] {
    const years = new Set<number>();

    pygData.forEach(item => {
      const [, year] = item.mes_ejercicio.split('/');
      const yearNum = parseInt(year);
      if (!isNaN(yearNum)) {
        years.add(yearNum);
      }
    });

    return Array.from(years).sort((a, b) => b - a); // Descending order (most recent first)
  }

  /**
   * Process P&G data for multiple fiscal years (ejercicios)
   * @param pygGrupos List of P&G concept groups
   * @param pygData List of P&G data values
   * @param fiscalYears Array of fiscal years to process (e.g., [2023, 2024, 2025])
   * @returns Processed multi-year table data and chart data
   */
  processDataForMultipleYears(
    pygGrupos: PygGrupo[],
    pygData: PygData[],
    fiscalYears: number[]
  ): {
    tableData: MultiYearPygTableRow[],
    chartData: { [year: string]: PygChartSegment[] },
    years: number[],
    totalSalesByYear: { [year: string]: number },
    resultsByYear: { [year: string]: number }
  } {
    // Sort years in ascending order
    const sortedYears = [...fiscalYears].sort((a, b) => a - b);

    // Process data for each year
    const dataByYear: { [year: string]: ReturnType<typeof this.processDataForYear> } = {};
    const totalSalesByYear: { [year: string]: number } = {};
    const resultsByYear: { [year: string]: number } = {};

    sortedYears.forEach(year => {
      const yearData = this.processDataForYear(pygGrupos, pygData, year);
      dataByYear[year] = yearData;
      totalSalesByYear[year] = yearData.totalSales;
      resultsByYear[year] = yearData.result;
    });

    // Build multi-year table data
    const tableData: MultiYearPygTableRow[] = [];

    pygGrupos.forEach(grupo => {
      const row: MultiYearPygTableRow = {
        concept: grupo.concept,
        clave_num: grupo.clave_num,
        years: {},
        isResult: false
      };

      // Add data for each year
      sortedYears.forEach(year => {
        const yearTableData = dataByYear[year].tableData;
        const conceptData = yearTableData.find(item => item.clave_num === grupo.clave_num);

        if (conceptData) {
          row.years[year] = {
            value: conceptData.value,
            percentage: conceptData.percentage,
            isEmpty: conceptData.isEmpty
          };
        } else {
          row.years[year] = {
            value: 0,
            percentage: 0,
            isEmpty: true
          };
        }
      });

      // Calculate variation if there are exactly 2 years
      if (sortedYears.length === 2) {
        const firstYear = sortedYears[0];
        const lastYear = sortedYears[1];
        const firstValue = row.years[firstYear].value;
        const lastValue = row.years[lastYear].value;

        row.variation = {
          value: lastValue - firstValue,
          percentage: firstValue !== 0
            ? ((lastValue - firstValue) / Math.abs(firstValue)) * 100
            : 0
        };
      }

      tableData.push(row);
    });

    // Add result row
    const resultRow: MultiYearPygTableRow = {
      concept: "Resultado",
      clave_num: "resultado",
      years: {},
      isResult: true
    };

    sortedYears.forEach(year => {
      const yearResult = resultsByYear[year];
      const yearTotalSales = totalSalesByYear[year];
      const percentage = yearTotalSales !== 0 ? (yearResult / yearTotalSales) * 100 : 0;

      resultRow.years[year] = {
        value: yearResult,
        percentage: percentage,
        isEmpty: false
      };
    });

    // Calculate variation for result row
    if (sortedYears.length === 2) {
      const firstYear = sortedYears[0];
      const lastYear = sortedYears[1];
      const firstValue = resultRow.years[firstYear].value;
      const lastValue = resultRow.years[lastYear].value;

      resultRow.variation = {
        value: lastValue - firstValue,
        percentage: firstValue !== 0
          ? ((lastValue - firstValue) / Math.abs(firstValue)) * 100
          : 0
      };
    }

    tableData.push(resultRow);

    // Extract chart data for each year
    const chartData: { [year: string]: PygChartSegment[] } = {};
    sortedYears.forEach(year => {
      chartData[year] = dataByYear[year].chartData;
    });

    return {
      tableData,
      chartData,
      years: sortedYears,
      totalSalesByYear,
      resultsByYear
    };
  }
}
